import { browserLocalPersistence, browserSessionPersistence, sendPasswordResetEmail, setPersistence, signInWithEmailAndPassword, signOut as fbSignOut } from 'firebase/auth';
import { IS_DEMO } from '@/demo/demoMode';
import { getFirebase } from './firebase';

/** הודעות שגיאה ברורות בעברית לשגיאות ההתחברות הנפוצות של Firebase. */
export function authErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return 'האימייל או הסיסמה שגויים';
    case 'auth/invalid-email':
      return 'כתובת האימייל אינה תקינה';
    case 'auth/missing-password':
      return 'יש להזין סיסמה';
    case 'auth/too-many-requests':
      return 'יותר מדי ניסיונות התחברות. יש להמתין מספר דקות ולנסות שוב';
    case 'auth/user-disabled':
      return 'החשבון הושבת. יש לפנות למנהל המערכת';
    case 'auth/network-request-failed':
      return 'אין חיבור לאינטרנט. יש לבדוק את החיבור ולנסות שוב';
    case 'auth/operation-not-allowed':
      return 'כניסה באימייל וסיסמה לא מופעלת בפרויקט Firebase (Authentication ← Sign-in method ← Email/Password)';
    case 'auth/unauthorized-domain':
      return 'כתובת האתר לא מאושרת ב-Firebase (Authentication ← Settings ← Authorized domains)';
    default:
      // הקוד מוצג כדי שאפשר יהיה לאבחן תקלה בהגדרות (למשל הגבלה על מפתח ה-API)
      return code ? `ההתחברות נכשלה (${code}). יש לנסות שוב` : 'ההתחברות נכשלה. יש לנסות שוב';
  }
}

function codeOf(err: unknown): string | undefined {
  return typeof err === 'object' && err !== null && 'code' in err ? String((err as { code: unknown }).code) : undefined;
}

/**
 * remember=true: נשאר מחובר במכשיר גם אחרי סגירת הדפדפן (עד יציאה ידנית).
 * remember=false: ההתחברות נמחקת כשסוגרים את הדפדפן — למחשב משותף.
 */
export async function signIn(email: string, password: string, remember = true): Promise<void> {
  try {
    const { auth } = getFirebase();
    await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
    await signInWithEmailAndPassword(auth, email.trim(), password);
  } catch (err) {
    throw new Error(authErrorMessage(codeOf(err)));
  }
}

export async function signOut(): Promise<void> {
  await fbSignOut(getFirebase().auth);
}

/**
 * שליחת קישור לאיפוס סיסמה. מחזיר הצלחה גם אם האימייל לא קיים —
 * כדי שלא ניתן יהיה לברר אילו כתובות רשומות במערכת.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  if (IS_DEMO) return; // מצב תצוגה: לא נשלח אימייל
  try {
    await sendPasswordResetEmail(getFirebase().auth, email.trim());
  } catch (err) {
    const code = codeOf(err);
    if (code === 'auth/user-not-found') return;
    if (code === 'auth/invalid-email' || code === 'auth/missing-email' || code === 'auth/network-request-failed' || code === 'auth/too-many-requests') {
      throw new Error(authErrorMessage(code === 'auth/missing-email' ? 'auth/invalid-email' : code));
    }
    throw new Error('לא הצלחנו לשלוח את הקישור. יש לנסות שוב');
  }
}
